import { Component, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DashboardService } from '../../../services/dashboard.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { buildDashboardThemeVars } from '../../../shared/dashboard-widget-theme';

interface RankedRow { label: string; value: number; pct: number; }
interface RankedCard { key: string; title: string; icon: string; accent: string; wide: boolean; rows: RankedRow[]; total: number; }

@Component({
	selector: 'app-dashboard-cat-line',
	templateUrl: './dashboard-cat-line.component.html',
	styleUrls: ['./dashboard-cat-line.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardCatLineComponent implements OnDestroy {

	private static readonly TOP_N = 10;

	cards: RankedCard[] = [];
	loading = false;
	useQty = false;
	config: any = false;
	private configSub: Subscription;
	private catlineSub: Subscription;

	constructor(
		private dashboardService: DashboardService,
		private globalSearchService: GlobalSearchService,
		private cdr: ChangeDetectorRef
	) {
		this.configSub = this.globalSearchService.configsubscription.subscribe(r => {
			if (r) {
				this.config = r;
				this.useQty = this.config?.env?.package === 'tires';
				this.loadData();
			}
		});
	}

	ngOnDestroy() {
		this.configSub?.unsubscribe();
		this.catlineSub?.unsubscribe();
	}

	dclTheme(accent: string): Record<string, string> {
		return buildDashboardThemeVars('dcl', accent);
	}

	loadData() {
		this.loading = true;
		this.cdr.markForCheck();
		this.catlineSub?.unsubscribe();
		this.catlineSub = this.dashboardService.getCatAndLinesPOS().subscribe((results: any) => {
			this.buildCards(results || {});
			this.loading = false;
			this.cdr.markForCheck();
		});
	}

	/** Normalize raw rows: parse values, relabel blanks/N/A, drop zero rows. */
	private cleanRows(data: any[]): Array<{ label: string; value: number }> {
		return (Array.isArray(data) ? data : [])
			.map((r: any) => {
				const raw = (r?.label || '').trim();
				const label = raw === '' || raw.toUpperCase() === 'N/A' ? 'Unspecified' : raw;
				const value = parseFloat(this.useQty ? r?.qty : r?.value) || 0;
				return { label, value };
			})
			.filter(r => r.value > 0);
	}

	private toRanked(rows: Array<{ label: string; value: number }>): RankedRow[] {
		const max = Math.max(1, ...rows.map(r => r.value));
		return rows.map(r => ({ ...r, pct: Math.max((r.value / max) * 100, 2) }));
	}

	private buildCards(results: any): void {
		const top = this.cleanRows(results.line_sales)
			.sort((a, b) => b.value - a.value)
			.slice(0, DashboardCatLineComponent.TOP_N);
		const low = this.cleanRows(results.line_sales_reversed)
			.sort((a, b) => a.value - b.value)
			.slice(0, DashboardCatLineComponent.TOP_N);
		const cat = this.cleanRows(results.category_sales)
			.sort((a, b) => b.value - a.value);

		this.cards = [
			{ key: 'top', title: 'Product Line Sales Top 10', icon: 'leaderboard', accent: 'danger', wide: false, rows: this.toRanked(top), total: top.reduce((s, r) => s + r.value, 0) },
			{ key: 'low', title: 'Product Line Sales Lowest 10', icon: 'trending_down', accent: 'warning', wide: false, rows: this.toRanked(low), total: low.reduce((s, r) => s + r.value, 0) },
			{ key: 'cat', title: 'Category Sales', icon: 'donut_small', accent: 'info', wide: true, rows: this.toRanked(cat), total: cat.reduce((s, r) => s + r.value, 0) },
		];
	}
}
